/**
 * Generer une cle SSH Ed25519 dans le coffre web (« Generate SSH key » de Bitwarden, de 1Password et de Proton Pass ;
 * `klavox ssh keygen` cote CLI, R21).
 *
 * La primitive est eprouvee, jamais maison : la paire vient de WebCrypto (`crypto.subtle.generateKey` en
 * `Ed25519`, RFC 8032 / RFC 8410). Ce module n'ecrit AUCUNE arithmetique de courbe : il extrait la graine du PKCS#8
 * exporte et la serialise au format `openssh-key-v1` NON chiffre (PROTOCOL.key d'OpenSSH, RFC 4251 §5), exactement
 * comme `serializeOpenSshPrivateKey` de la CLI. Non chiffre a dessein : la cle vit dans les secrets de l'entree,
 * scellee avec elle ; c'est la forme que l'agent `klavox ssh agent` sait servir.
 *
 * La cle privee n'existe qu'en memoire, le temps d'etre rendue au brouillon de l'editeur ; les tampons
 * intermediaires (PKCS#8, graine, corps OpenSSH) sont effaces avant de rendre la main.
 */
import { bytesToBase64, randomBytes } from '@klavox/crypto-core';
import type { SshKeyMaterial } from './ssh-key-item.js';

const ALGORITHM = 'ssh-ed25519';
/** En-tete PKCS#8 fixe d'une cle privee Ed25519 (RFC 8410 §7) ; la graine de 32 octets le suit. */
export const PKCS8_ED25519_PREFIX = Uint8Array.from([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]);

export class SshKeygenError extends Error {
  override readonly name = 'SshKeygenError';
}

const utf8 = (text: string): Uint8Array => new TextEncoder().encode(text);

export function concat(parts: readonly Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

/** `string` SSH : longueur sur 32 bits big-endian, puis les octets. */
export function sshString(value: Uint8Array | string): Uint8Array {
  const bytes = typeof value === 'string' ? utf8(value) : value;
  const out = new Uint8Array(4 + bytes.length);
  new DataView(out.buffer).setUint32(0, bytes.length);
  out.set(bytes, 4);
  return out;
}

/** Commentaire sur une ligne : il finit dans la ligne publique, un saut de ligne la casserait. */
function cleanComment(comment: string): string {
  return comment.replace(/\s+/gu, ' ').trim();
}

/** Le navigateur sait-il generer une paire Ed25519 ? (Chrome 137+, Firefox 129+, Safari 17+.) */
export async function canGenerateSshKey(subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle): Promise<boolean> {
  if (subtle === undefined) return false;
  try {
    await subtle.generateKey({ name: 'Ed25519' }, false, ['sign', 'verify']);
    return true;
  } catch {
    return false;
  }
}

/** Corps `openssh-key-v1` non chiffre d'une paire Ed25519 (graine 32 + publique 32). */
function openSshPrivateBody(seed: Uint8Array, publicKey: Uint8Array, comment: string): Uint8Array {
  const publicBlob = concat([sshString(ALGORITHM), sshString(publicKey)]);
  // `check` n'est pas un secret : OpenSSH le compare a lui-meme pour detecter une mauvaise phrase de passe.
  const check = randomBytes(4);
  const secret = concat([seed, publicKey]);
  const inner = concat([check, check, sshString(ALGORITHM), sshString(publicKey), sshString(secret), sshString(comment)]);
  secret.fill(0);
  // Remplissage 1, 2, 3... jusqu'au multiple de 8 (taille de bloc du chiffrement « none »).
  const padding = (8 - (inner.length % 8)) % 8;
  const padded = concat([inner, Uint8Array.from({ length: padding }, (_, i) => i + 1)]);
  inner.fill(0);
  const body = concat([
    utf8('openssh-key-v1\0'),
    sshString('none'),
    sshString('none'),
    sshString(''),
    Uint8Array.from([0, 0, 0, 1]),
    sshString(publicBlob),
    sshString(padded),
  ]);
  padded.fill(0);
  return body;
}

/**
 * Genere une paire Ed25519 et la rend sous la forme des secrets d'une entree `ssh-key` : bloc
 * `-----BEGIN OPENSSH PRIVATE KEY-----` et ligne `ssh-ed25519 AAAA… commentaire`.
 *
 * @throws {SshKeygenError} navigateur sans Ed25519 dans WebCrypto, ou export inattendu.
 */
export async function generateSshKey(
  comment: string,
  subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle,
): Promise<SshKeyMaterial> {
  if (subtle === undefined) throw new SshKeygenError('WebCrypto indisponible : generation de cle impossible');
  let pair: CryptoKeyPair;
  try {
    pair = (await subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify'])) as CryptoKeyPair;
  } catch (cause) {
    throw new SshKeygenError('Ce navigateur ne sait pas generer de cle Ed25519 : mettez-le a jour, ou collez une cle', { cause });
  }
  const pkcs8 = new Uint8Array(await subtle.exportKey('pkcs8', pair.privateKey));
  const publicKey = new Uint8Array(await subtle.exportKey('raw', pair.publicKey));
  try {
    const prefixOk =
      pkcs8.length === PKCS8_ED25519_PREFIX.length + 32 && PKCS8_ED25519_PREFIX.every((byte, i) => pkcs8[i] === byte);
    if (!prefixOk || publicKey.length !== 32) throw new SshKeygenError('export Ed25519 inattendu');
    return openSshEd25519Key(pkcs8.subarray(PKCS8_ED25519_PREFIX.length), publicKey, comment);
  } finally {
    pkcs8.fill(0);
  }
}

/**
 * Paire Ed25519 (graine + cle publique deja obtenues d'une primitive eprouvee) mise en forme d'entree `ssh-key` :
 * bloc `openssh-key-v1` non chiffre et ligne publique. Sert a la generation comme a l'import CXF (`ssh-key-pkcs8.ts`).
 */
export function openSshEd25519Key(seed: Uint8Array, publicKey: Uint8Array, comment: string): SshKeyMaterial {
  if (seed.length !== 32 || publicKey.length !== 32) throw new SshKeygenError('paire Ed25519 inattendue');
  const line = cleanComment(comment);
  const body = openSshPrivateBody(seed, publicKey, line);
  const base64 = bytesToBase64(body).replace(/(.{70})/gu, '$1\n');
  body.fill(0);
  const publicLine = `${ALGORITHM} ${bytesToBase64(concat([sshString(ALGORITHM), sshString(publicKey)]))}`;
  return {
    privateKey: `-----BEGIN OPENSSH PRIVATE KEY-----\n${base64}\n-----END OPENSSH PRIVATE KEY-----\n`,
    publicKey: line.length > 0 ? `${publicLine} ${line}` : publicLine,
  };
}
